import {
  addMonths,
  differenceInDays,
  differenceInWeeks,
  formatDistanceStrict,
  isBefore,
} from 'date-fns'
import {
  SessionFragment,
  TrainingsSessionAttendanceStatusEnum,
  TrainingsSessionModalityEnum,
  TrainingsTrainingMembershipStatusEnum,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import i18n from 'modules/core/lib/i18n'

export const TRAINING_MEMBERSHIP_STATUS = {
  COMPLETED: 'completed' as const,
  MISSING: 'missing' as const,
  NOT_ASSIGNED: 'notassigned' as const,
  NOT_STARTED: 'notstarted' as const,
  PARTIALLY_COMPLETED: 'partiallycompleted' as const,
  STARTED: 'started' as const,
} as const

const SESSION_ATTENDANCE_STATUS = {
  COMPLETED: 'completed' as const,
  IN_PROGRESS: 'inprogress' as const,
  MISSING: 'missing' as const,
  PENDING: 'pending' as const,
} as const

export type TrainingAttendanceStatus =
  | 'notstarted'
  | 'started'
  | 'partiallycompleted'
  | 'completed'
  | 'missing'
  | 'notassigned'

const getTrainingAttendanceStatus = (
  statuses: TrainingsSessionAttendanceStatusEnum[]
): TrainingsTrainingMembershipStatusEnum => {
  const uniqueStatuses = [...new Set(statuses)]

  if (uniqueStatuses.length === 1) {
    const status = uniqueStatuses[0]
    if (status === SESSION_ATTENDANCE_STATUS.COMPLETED) {
      return TRAINING_MEMBERSHIP_STATUS.COMPLETED
    } else if (status === SESSION_ATTENDANCE_STATUS.MISSING) {
      return TRAINING_MEMBERSHIP_STATUS.MISSING
    } else if (status === SESSION_ATTENDANCE_STATUS.PENDING) {
      return TRAINING_MEMBERSHIP_STATUS.NOT_STARTED
    }
  }
  if (uniqueStatuses.includes(SESSION_ATTENDANCE_STATUS.IN_PROGRESS)) {
    return TRAINING_MEMBERSHIP_STATUS.STARTED
  }

  return TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED
}

export const useTrainingsDatatable = () => {
  const getModalityTranslation = (modality?: TrainingsSessionModalityEnum | null) => {
    switch (modality) {
      case 'inperson':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.inperson')
      case 'mixed':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.mixed')
      case 'online':
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.online')
      default:
        return i18n.t('trainings.my_trainings_revamp.overview.modalities.none')
    }
  }
  const getModality = (
    modality: TrainingsSessionModalityEnum | null | undefined
  ): TrainingsSessionModalityEnum | undefined => {
    if (!modality) {
      return undefined
    }

    return modality
  }

  const getSessionAttendanceTranslation = (status?: TrainingsSessionAttendanceStatusEnum) => {
    switch (status) {
      case SESSION_ATTENDANCE_STATUS.PENDING:
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.not_started')
      case SESSION_ATTENDANCE_STATUS.COMPLETED:
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.attended')
      case SESSION_ATTENDANCE_STATUS.IN_PROGRESS:
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.in_progress')
      case SESSION_ATTENDANCE_STATUS.MISSING:
        return i18n.t('trainings.my_trainings_revamp.session_attendance_status.did_not_attend')
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }
  const getTrainingAttendanceTranslation = (status: TrainingsTrainingMembershipStatusEnum) => {
    switch (status) {
      case TRAINING_MEMBERSHIP_STATUS.NOT_STARTED:
        return i18n.t('trainings.my_training.all_courses_table.not_started')
      case TRAINING_MEMBERSHIP_STATUS.COMPLETED:
        return i18n.t('trainings.my_training.all_courses_table.completed')
      case TRAINING_MEMBERSHIP_STATUS.STARTED:
        return i18n.t('trainings.my_training.all_courses_table.started')
      case TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED:
        return i18n.t('trainings.my_training.all_courses_table.partially_completed')
      case TRAINING_MEMBERSHIP_STATUS.MISSING:
        return i18n.t('trainings.my_training.all_courses_table.absent')
      case TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED:
        return i18n.t('trainings.my_training.all_courses_table.not_assigned')
      default:
        throw new Error(`Unknown training attendance status: ${status}`)
    }
  }

  function parseAttendanceStatus(status: string): TrainingsSessionAttendanceStatusEnum {
    switch (status) {
      case SESSION_ATTENDANCE_STATUS.PENDING:
      case SESSION_ATTENDANCE_STATUS.IN_PROGRESS:
      case SESSION_ATTENDANCE_STATUS.COMPLETED:
      case SESSION_ATTENDANCE_STATUS.MISSING:
        return status
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getSessionAttendanceColor = (status: TrainingsSessionAttendanceStatusEnum) => {
    switch (status) {
      case SESSION_ATTENDANCE_STATUS.COMPLETED:
        return 'lime'
      case SESSION_ATTENDANCE_STATUS.IN_PROGRESS:
        return 'malibu'
      case SESSION_ATTENDANCE_STATUS.MISSING:
        return 'radical'
      case SESSION_ATTENDANCE_STATUS.PENDING:
        return 'neutral'
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getTrainingAttendanceColor = (status: TrainingsTrainingMembershipStatusEnum) => {
    switch (status) {
      case TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED:
        return 'champagne'
      case TRAINING_MEMBERSHIP_STATUS.COMPLETED:
        return 'lime'
      case TRAINING_MEMBERSHIP_STATUS.STARTED:
        return 'malibu'
      case TRAINING_MEMBERSHIP_STATUS.MISSING:
        return 'radical'
      case TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED:
        return 'tangerine'
      case TRAINING_MEMBERSHIP_STATUS.NOT_STARTED:
        return 'neutral'
      default:
        throw new Error(`Unknown training attendance status: ${status}`)
    }
  }

  const getDataCollectionSessionAttendanceColor = (
    status?: TrainingsSessionAttendanceStatusEnum
  ) => {
    switch (status) {
      case SESSION_ATTENDANCE_STATUS.COMPLETED:
        return 'positive'
      case SESSION_ATTENDANCE_STATUS.IN_PROGRESS:
        return 'info'
      case SESSION_ATTENDANCE_STATUS.MISSING:
        return 'critical'
      case SESSION_ATTENDANCE_STATUS.PENDING:
        return 'neutral'
      default:
        throw new Error(`Unknown session attendance status: ${status}`)
    }
  }

  const getDataCollectionTrainingAttendanceColor = (
    status?: TrainingsTrainingMembershipStatusEnum
  ) => {
    switch (status) {
      case TRAINING_MEMBERSHIP_STATUS.COMPLETED:
        return 'positive'
      case TRAINING_MEMBERSHIP_STATUS.STARTED:
        return 'info'
      case TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED:
        return 'warning'
      case TRAINING_MEMBERSHIP_STATUS.MISSING:
        return 'critical'
      case TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED:
        return 'warning'
      case TRAINING_MEMBERSHIP_STATUS.NOT_STARTED:
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  const mapTrainingAttendanceStatus = (
    attendanceStatus: TrainingsTrainingMembershipStatusEnum
  ): TrainingsTrainingMembershipStatusEnum => {
    switch (attendanceStatus) {
      case TRAINING_MEMBERSHIP_STATUS.COMPLETED:
      case TRAINING_MEMBERSHIP_STATUS.NOT_STARTED:
      case TRAINING_MEMBERSHIP_STATUS.STARTED:
      case TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED:
      case TRAINING_MEMBERSHIP_STATUS.MISSING:
      case TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED:
        return attendanceStatus
      default:
        return TRAINING_MEMBERSHIP_STATUS.NOT_STARTED
    }
  }

  const collectModalities = (sessions: SessionFragment[]) => {
    const modalities = sessions
      .map((session) => session.modality)
      .filter(Boolean)
      .map((modality) => getModalityTranslation(modality))

    return Array.from(new Set(modalities)).join(', ')
  }

  const getModalityValue = (
    modalities: (TrainingsSessionModalityEnum | undefined)[]
  ): TrainingsSessionModalityEnum | undefined => {
    return uniq(modalities).length === 1 && modalities[0] ? modalities[0] : undefined
  }

  const getDueDateText = (dueDate: string | null | undefined): string => {
    if (!dueDate) {
      return i18n.t('trainings.trainings_revamp.training_detail.employees_table.empty_due_date')
    }

    const today = new Date()
    const dueDateObj = new Date(dueDate)

    const timeDifferenceText = (days: number, weeks: number) => {
      if (days < 7) {
        return i18n.tp('trainings.trainings_revamp.training_detail.employees_table.due_date.days', {
          count: days,
        })
      } else if (days < 30) {
        return i18n.tp(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.weeks',
          {
            count: weeks,
          }
        )
      } else {
        return `${i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.in'
        )} ${formatDistanceStrict(dueDateObj, today)}`
      }
    }

    const isOverdue = isBefore(dueDateObj, today)
    const daysDifference = differenceInDays(dueDateObj, today)
    const weeksDifference = differenceInWeeks(dueDateObj, today)

    if (isOverdue) {
      return i18n.t('trainings.trainings_revamp.training_detail.employees_table.due_date.overdue')
    } else if (daysDifference < 30) {
      return timeDifferenceText(daysDifference, weeksDifference)
    } else {
      return `${i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.due_date.in'
      )} ${formatDistanceStrict(dueDateObj, today)}`
    }
  }

  const getDueDateStatus = (
    dueDate: string | null | undefined
  ): { status: 'critical' | 'warning' | 'neutral'; label: string } | null => {
    if (!dueDate) {
      return null
    }

    const today = new Date()
    const dueDateObj = new Date(dueDate)

    const timeDifferenceText = (days: number, weeks: number) => {
      if (days < 7) {
        return i18n.tp('trainings.trainings_revamp.training_detail.employees_table.due_date.days', {
          count: days,
        })
      } else if (days < 30) {
        return i18n.tp(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.weeks',
          {
            count: weeks,
          }
        )
      } else {
        return `${i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.in'
        )} ${formatDistanceStrict(dueDateObj, today)}`
      }
    }

    const isOverdue = isBefore(dueDateObj, today)
    const daysDifference = differenceInDays(dueDateObj, today)
    const weeksDifference = differenceInWeeks(dueDateObj, today)

    if (isOverdue) {
      return {
        status: 'critical',
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.overdue'
        ),
      }
    } else if (daysDifference < 30) {
      return {
        status: 'warning',
        label: timeDifferenceText(daysDifference, weeksDifference),
      }
    } else {
      const isWithinThreeMonths = isBefore(dueDateObj, addMonths(today, 3))
      return {
        status: isWithinThreeMonths ? 'warning' : 'neutral',
        label: `${i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.due_date.in'
        )} ${formatDistanceStrict(dueDateObj, today)}`,
      }
    }
  }

  const getDueDateTagColor = (dueDate: string): 'radical' | 'tangerine' | 'grey4' => {
    const today = new Date()
    const dueDateObj = new Date(dueDate)

    const isOverdue = isBefore(dueDateObj, today)
    const daysDifference = differenceInDays(dueDateObj, today)

    if (isOverdue) {
      return 'radical'
    } else if (daysDifference < 30) {
      return 'tangerine'
    } else {
      const isWithinThreeMonths = isBefore(dueDateObj, addMonths(today, 3))
      return isWithinThreeMonths ? 'tangerine' : 'grey4'
    }
  }

  return {
    collectModalities,
    getSessionAttendanceColor,
    getTrainingAttendanceColor,
    getSessionAttendanceTranslation,
    getTrainingAttendanceTranslation,
    getModalityTranslation,
    getDataCollectionSessionAttendanceColor,
    getDataCollectionTrainingAttendanceColor,
    getModality,
    getTrainingAttendanceStatus,
    getModalityValue,
    parseAttendanceStatus,
    mapTrainingAttendanceStatus,
    getDueDateText,
    getDueDateStatus,
    getDueDateTagColor,
  }
}
